<script setup>
import { reactive } from 'vue';
import { router } from '@inertiajs/vue3';
import { useI18n } from "vue-i18n";
import JetDropdown from '@/Jetstream/Dropdown.vue';
import JetDropdownLink from '@/Jetstream/DropdownLink.vue';
import { useStore } from 'vuex';
import CountryFlag from 'vue-country-flag-next';

const props = defineProps({
    currentLocale: String,
    availableLocales: Array
});

const store = useStore();
const selectedLanguage = reactive(store.state.selectedLanguage);
const currentLanguage = reactive(store.state.currentSiteLanguage);
const { locale } = useI18n({ useScope: 'global' });

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
let availableLocales = [];

if(organisation && organisation.available_locales) {
    availableLocales = organisation.available_locales;
} else if (programme && programme.available_locales) {
    availableLocales = programme.available_locales;
} else if (props.availableLocales) {
    availableLocales = props.availableLocales;
}

function setLocale(newLocale, selected) {
    router.post("/api/locale", {
        'language': newLocale,
    }, {
        preserveState: false,
        replace: true,
        preserveScroll: false,
        forceFormData: true
    });
    store.commit('switchLanguage', newLocale);
    store.commit('maskAsSelected', selected);
    locale.value = newLocale;
}

function clearLocale(all = false) {
    if (all) {
        store.commit('clearLanguageSelection');
        window.localStorage.removeItem('vuex');

        //set back to default which is always english
        setLocale('en', null);
    } else if (selectedLanguage && selectedLanguage == 'picker') {
        store.commit('clearLanguageSelection');
        window.localStorage.removeItem('vuex');
    }
}

if (!currentLanguage) {
    //check if we're logged in and if we are set to user preference
    if (router.page.props.auth.user && router.page.props.auth.user.preferred_language) {
        setLocale(router.page.props.auth.user.preferred_language, 'user');
    } else {
        //check if we need to refresh the organisation/programme language due to admin changes
        if ((organisation && organisation.preferred_language) && organisation.preferred_language != currentLanguage) {
            setLocale(organisation.preferred_language, 'organisation');
        } else if ((programme && programme.preferred_language) && programme.preferred_language != currentLanguage) {
            setLocale(programme.preferred_language, 'programme');
        } else {
            clearLocale(true);
        }
    }
} else {
    if (selectedLanguage == 'programme' || selectedLanguage == 'organisation') { //check if we have a user
        if (router.page.props.auth.user && router.page.props.auth.user.preferred_language) {
            setLocale(router.page.props.auth.user.preferred_language, 'user');
        } else {
            //check if we need to refresh the organisation/programme language due to admin changes
            if (organisation && organisation.preferred_language) {
                if (organisation.preferred_language != currentLanguage) {
                    setLocale(organisation.preferred_language, 'organisation');
                }
            } else if (programme && programme.preferred_language) {
                if (programme.preferred_language != currentLanguage) {
                    setLocale(programme.preferred_language, 'programme');
                }
            } else {
                clearLocale(true);
            }
        }
    } else if (!selectedLanguage) {
        if (router.page.props.auth.user && router.page.props.auth.user.preferred_language) {
            setLocale(router.page.props.auth.user.preferred_language, 'user');
        } else {
            //check if we need to refresh the organisation/programme language due to admin changes
            if ((organisation && organisation.preferred_language) && organisation.preferred_language != currentLanguage) {
                setLocale(organisation.preferred_language, 'organisation');
            } else if ((programme && programme.preferred_language) && programme.preferred_language != currentLanguage) {
                setLocale(programme.preferred_language, 'programme');
            }
        }
    }
}

</script>

<template>
    <!-- {{ $t("toolkit.nav.language_full") }} -->
    <JetDropdown v-if="availableLocales" align="right" width="60">
        <template #trigger>
            <span class="inline-flex rounded-md">
                <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50 transition">
                    <!-- {{ $page.props.auth.user.current_team.name }} -->
                    <span class="material-icons-outlined">language</span>
                    <CountryFlag v-if="selectedLanguage && selectedLanguage == 'picker'" :country="locale == 'cy' ? 'gb-wls' : locale === 'en' ? 'gb-eng' : locale" size='small'/>
                    <svg
                        class="ml-2 -mr-0.5 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </button>
            </span>
        </template>

        <template #content>
            <div class="w-60">
                <template v-for="(option, index) in availableLocales" :locale="locale">
                    <div>
                        <JetDropdownLink v-on:click="setLocale(index, 'picker')">
                            <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px;">
                                <div>
                                    <CountryFlag :country="index == 'cy' ? 'gb-wls' : index === 'en' ? 'gb-eng' : index" size='normal'/>
                                </div>
                                <div style="flex-grow: 10;">
                                    {{ option }}
                                </div>
                                <div v-if="index === locale && selectedLanguage == 'picker'" style="align-self: center;">
                                    <span class="material-icons-outlined">done</span>
                                </div>
                            </div>
                        </JetDropdownLink>
                    </div>

                </template>
                <div style="border-top: 1px solid #c7c7c7;">
                    <JetDropdownLink v-on:click="clearLocale()">
                        {{$t('application.navigation.clear_selection')}}
                    </JetDropdownLink>
                </div>
            </div>
        </template>
    </JetDropdown>
</template>
